import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { UNITS, normalizeUnit, unitLabel } from '../utils/units';
import PillToggleGroup from './ui/PillToggleGroup';

/**
 * WeightUnitToggle — segmented kg | lbs mounted in the Header settings
 * dropdown (and the mobile drawer), for coaches AND students.
 * Coach: « Mon unité » = unit he types in, unit of his templates, default unit
 * of his new students. Student: unit of his gym.
 * Optimistic write through AuthContext.setWeightUnit (no toast, no spinner),
 * same contract as LanguageToggle.
 */
const baseButtonStyle = {
  flex: 1,
  padding: '6px 10px',
  fontSize: '13px',
  fontWeight: 500,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  transition: 'background-color 0.2s, color 0.2s',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.55)',
};

const activeButtonStyle = {
  ...baseButtonStyle,
  background: 'rgba(212, 132, 90, 0.18)',
  color: '#d4845a',
};

// variant 'pill' : pastilles cuivre du volet Réglages coach (SettingsSidebar).
const WeightUnitToggle = ({ variant }) => {
  const { t } = useTranslation();
  const { user, setWeightUnit } = useAuth();
  const current = normalizeUnit(user?.weightUnit);

  const handleSelect = (next) => {
    if (next === current) return;
    setWeightUnit?.(next);
  };

  if (variant === 'pill') {
    return (
      <PillToggleGroup
        ariaLabel={t('settings.weight_unit_label')}
        options={UNITS.map((unit) => ({ value: unit, label: unitLabel(unit) }))}
        value={current}
        onSelect={handleSelect}
        testIdPrefix="weight-unit"
      />
    );
  }

  return (
    <div
      role="group"
      aria-label={t('settings.weight_unit_label')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
        padding: '4px',
        marginBottom: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        borderRadius: '8px',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {UNITS.map((unit) => {
        const isActive = unit === current;
        return (
          <button
            key={unit}
            type="button"
            aria-pressed={isActive}
            onClick={() => handleSelect(unit)}
            style={isActive ? activeButtonStyle : baseButtonStyle}
          >
            {unitLabel(unit)}
          </button>
        );
      })}
    </div>
  );
};

export default WeightUnitToggle;
